/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Events` for the Caintra Connect frontend.
 * @module pages/Events
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/Events.module.css";

import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";

import { CircularProgress, Backdrop } from "@mui/material";

import { use, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  getData,
  updateAttendanceCancelledStatus,
  showEventCreatedSuccess,
} from "../logic/events";
import { registerForVirtualEvent } from "../logic/virtualEvents";
import { markCompanyAsInterested } from "../logic/breakfastEvents";

import { ReactComponent as TitleIcon } from "../assets/icons/Events/TitleIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/Events/CheckCircleIcon.svg";

import EventListItem from "../components/Events/EventListItem/index.jsx";
import EventRegistrationModal from "../modals/EventRegistrationModal/index.jsx";
import ViewRegistrationModal from "../modals/ViewRegistrationModal/index.jsx";
import ViewVirtualEventRegistrationModal from "../modals/ViewVirtualEventRegistrationModal/index.jsx";
import VirtualEventRegistrationModal from "../modals/VirtualEventRegistrationModal/index.jsx";
import AttendanceCancellationModal from "../modals/AttendanceCancellation/index.jsx";
import PaymentModal from "../modals/PaymentModal/index.jsx";
import { canAccessEvents } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const Events = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canAccessEvents);

  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [viewRegistrationModalOpen, setViewRegistrationModalOpen] =
    useState(false);
  const [cancelAttendanceModalOpen, setCancelAttendanceModalOpen] =
    useState(false);
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [virtualViewRegistrationOpen, setVirtualViewRegistrationOpen] =
    useState(false);
  const [virtualRegisterModalOpen, setVirtualRegisterModalOpen] =
    useState(false);
  const [virtualRegisterModalEvent, setVirtualRegisterModalEvent] =
    useState(null);
  const [virtualRegisterError, setVirtualRegisterError] = useState(null);

  // Device detection for responsive search bar
  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    } else if (isMediumMobile) {
      return "mediumMobile";
    } else if (isLaptop) {
      return "laptop";
    } else if (isTablet) {
      return "tablet";
    } else {
      return "desktop";
    }
  };

  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "98%";
    } else if (device === "tablet") {
      return "73%";
    } else if (device === "mediumMobile") {
      return "90%";
    } else if (device === "smallMobile") {
      return "75%";
    } else {
      return "98%";
    }
  };

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const initializeData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!!token && token.trim() !== "undefined") {
          await fetchData();
        }
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    initializeData();
  }, [allowed]);

  // Check if user just returned from creating an event
  useEffect(() => {
    if (location.state?.eventCreated) {
      handleShowEventCreatedSuccess();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Sync pre-register status when returning from the pre-register form
  useEffect(() => {
    const preRegisteredEventId = location.state?.preRegisteredEventId;
    if (!preRegisteredEventId) {
      return;
    }

    setEventsData((prev) =>
      prev.map((event) =>
        event._id === preRegisteredEventId
          ? { ...event, isUserRegistered: true }
          : event,
      ),
    );
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  const fetchData = async () => {
    try {
      const events = await getData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const refreshEvents = async () => {
    try {
      const events = await getData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
      setEventsData([]);
    }
  };

  const handleUpdateAttendanceCancelledStatus = async () => {
    await updateAttendanceCancelledStatus(
      eventsData,
      selectedEvent,
      setEventsData,
      setSelectedEvent,
      setCancelAttendanceModalOpen,
      navigate,
    );
  };

  /**
   * Gets the appropriate event cost based on the user's membership type.
   * Returns member pricing for user types 1, 2, 3, 5 and non-member pricing for type 4.
   * Falls back to default cost if specific pricing is not available.
   * @returns {number} The event cost in MXN
   */
  const getEventCost = (event) => {
    if (user?.tipoDeUsuario === "4") {
      return event.costoNoSocios || event.costo || 0;
    } else if (["1", "2", "3", "5"].includes(user?.tipoDeUsuario)) {
      return event.costoSocios || event.costo || 0;
    } else {
      return 0;
    }
  };

  const handleRegister = (event) => {
    setSelectedEvent(event);
    const currentEventCost = getEventCost(event);
    if (currentEventCost > 0) {
      setPaymentModalOpen(true);
    } else {
      setDisplayModalOpen(true);
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    if (
      direction === "next" &&
      currentPage < Math.ceil(eventsData.length / resultsLimit)
    ) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const filteredEventsList = eventsData?.filter((event) =>
    event.nombre
      ? event.nombre.toLowerCase().includes(searchQuery.toLowerCase())
      : false,
  );

  const paginatedEventsList = filteredEventsList.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  const handleViewRegistration = (event) => {
    setSelectedEvent(event);
    setViewRegistrationModalOpen(true);
  };

  const handleCancelAttendance = (event) => {
    setSelectedEvent(event);
    setCancelAttendanceModalOpen(true);
  };

  const handleViewBusinessEvent = (event) => {
    navigate(`/businessEvent/${event._id}`);
  };

  /** B2B events: open detail; enrichment loads on the detail screen only. */
  const handleRegisterB2B = (event) => {
    navigate(`/b2bEvent/${event._id}`);
  };

  const closeVirtualRegisterModal = useCallback(() => {
    setVirtualRegisterModalOpen(false);
    setVirtualRegisterModalEvent(null);
  }, []);

  const openVirtualRegisterModal = useCallback((event) => {
    setVirtualRegisterError(null);
    setVirtualRegisterModalEvent(event);
    setVirtualRegisterModalOpen(true);
  }, []);

  const handleVirtualRegisterModalSubmit = useCallback(
    async ({ nombre, telefono, codigoPais }) => {
      const id = virtualRegisterModalEvent?._id;
      if (!id) {
        return {
          success: false,
          error: t("pages.events.virtualRegisterErrorGeneric"),
        };
      }
      setVirtualRegisterError(null);
      const result = await registerForVirtualEvent(id, navigate, {
        nombre,
        telefono,
        codigoPais,
      });
      if (result?.error === "auth") {
        closeVirtualRegisterModal();
        return { success: false };
      }
      if (result?.success) {
        setEventsData((prev) =>
          prev.map((e) =>
            e._id === id && e.tipoEvento === "VIRTUAL_EVENT"
              ? { ...e, isUserRegistered: true }
              : e,
          ),
        );
        closeVirtualRegisterModal();
        return { success: true };
      }
      const message =
        result?.error || t("pages.events.virtualRegisterErrorGeneric");
      setVirtualRegisterError({ eventId: id, message });
      return { success: false, error: message };
    },
    [virtualRegisterModalEvent, navigate, t, closeVirtualRegisterModal],
  );

  const handleViewVirtualRegistration = (event) => {
    setVirtualRegisterError(null);
    setSelectedEvent(event);
    setVirtualViewRegistrationOpen(true);
  };

  const handleBreakfastEventRegistration = async (event) => {
    try {
      const result = await markCompanyAsInterested(event._id, navigate);
      if (result) {
        setEventsData((prev) =>
          prev.map((e) =>
            e._id === event._id
              ? {
                  ...e,
                  puedeRegistrarse: false,
                  registradoComoInteresado: true,
                }
              : e,
          ),
        );
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const handleShowEventCreatedSuccess = () => {
    showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and search bar */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>{t("pages.events.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <SearchBar
              placeholder={
                isSmallMobile || isMediumMobile
                  ? t("pages.events.mobileSearchText")
                  : t("pages.events.searchText")
              }
              value={searchQuery}
              onChange={handleSearch}
              width={getSearchBarWidth(getDevice())}
            />
          </div>
        </div>
      </div>
      <div className={styles.eventsList}>
        {paginatedEventsList.length == 0 && (
          <div className={styles.noResults}>
            <p>{t("pages.events.noResults")}</p>
          </div>
        )}
        {paginatedEventsList.length > 0 &&
          paginatedEventsList.map((event, index) => (
            <EventListItem
              key={event._id || index}
              event={event}
              index={index}
              user={user}
              t={t}
              styles={styles}
              navigate={navigate}
              getEventCost={getEventCost}
              virtualRegisterModalOpen={virtualRegisterModalOpen}
              virtualRegisterModalEvent={virtualRegisterModalEvent}
              virtualRegisterError={virtualRegisterError}
              onViewVirtualRegistration={handleViewVirtualRegistration}
              onOpenVirtualRegisterModal={openVirtualRegisterModal}
              onBreakfastEventRegistration={handleBreakfastEventRegistration}
              onViewRegistration={handleViewRegistration}
              onRegister={handleRegister}
              onCancelAttendance={handleCancelAttendance}
              onRegisterB2B={handleRegisterB2B}
            />
          ))}
      </div>
      <div className={styles.footer}>
        {/* Number of results dropdown */}
        <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />

        {/* Page selector */}
        <PageSelector
          currentPage={currentPage}
          totalPages={Math.ceil(filteredEventsList.length / resultsLimit)}
          onPageChange={handlePageChange}
        />
      </div>
      {displayModalOpen && (
        <EventRegistrationModal
          open={displayModalOpen}
          onClose={() => {
            setDisplayModalOpen(false);
            setSelectedEvent(null);
            refreshEvents();
          }}
          event={selectedEvent}
          user={user}
        />
      )}
      {paymentModalOpen && (
        <PaymentModal
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedEvent(null);
            refreshEvents();
          }}
          event={selectedEvent}
          user={user}
        />
      )}
      {viewRegistrationModalOpen && (
        <ViewRegistrationModal
          open={viewRegistrationModalOpen}
          onClose={() => setViewRegistrationModalOpen(false)}
          event={selectedEvent}
        />
      )}
      {virtualViewRegistrationOpen && (
        <ViewVirtualEventRegistrationModal
          open={virtualViewRegistrationOpen}
          onClose={() => {
            setVirtualViewRegistrationOpen(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
        />
      )}
      {virtualRegisterModalOpen && (
        <VirtualEventRegistrationModal
          open={virtualRegisterModalOpen}
          onClose={closeVirtualRegisterModal}
          onSubmit={handleVirtualRegisterModalSubmit}
        />
      )}
      <AttendanceCancellationModal
        open={cancelAttendanceModalOpen}
        onCancel={() => setCancelAttendanceModalOpen(false)}
        event={selectedEvent}
        onSuccess={handleUpdateAttendanceCancelledStatus}
      />
      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t("pages.events.creationMessage")}
        </div>
      )}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default Events;
